// 能源數字的格式（plan SPEC #energy-terms「數字格式」「誠實顯示」，設計稿 reviews/energy-design-2026-10-21 kit.py 的 kwh() / money() / approx()）。
// 全部純函式：輸入平台原始單位（mWh / mW / 主單位金額），輸出畫面字串；數字與單位、幣別之間是不換行空白（U+00A0）。
//   - 功率：< 1 W「不到 1 W」、< 10 W 一位小數、≥ 10 W 整數
//   - 用電：< 0.01「不到 0.01 度」、< 10 兩位小數、< 1000 一位、≥ 1000 整數加千分位
//   - 金額：TWD / JPY 整數（不到 1 元「不到 NT$ 1」/「不到 ¥1」）、CNY / USD 兩位；人民幣寫 CN¥
//   - 百分比：整數；門檻判斷用原始值、顯示無條件捨去（94.7% 不寫「超過 95%」）
//   - 預估 / 推估：2 位有效數字、至少到個位（約 130 度、約 12,000 kWh）
//   - 比較：四捨五入 0% 寫「持平」；VMS 用 ▲ ▼（朗讀「多 / 少」），上一期 < 1 度改寫絕對差
import type { TFunction } from 'i18next';

export const NBSP = '\u00a0';

/** 用電單位：team 組織寫 kWh；個人組織與簡易模式寫「度」（en / ja 一律 kWh，字串表決定）。 */
export type EnergyUnit = 'kwh' | 'du';

export interface Fmt {
  lang: string;
  unit: EnergyUnit;
  /** 單位字（kWh / 度） */
  unitText: string;
  /** mWh → 「7,903 kWh」 */
  energy(mwh: number | null | undefined): string;
  /** mWh → 只有數字（表格欄已寫單位時） */
  energyNum(mwh: number | null | undefined): string;
  /** mW → 「23 W」 */
  power(mw: number | null | undefined): string;
  /** W（PoE 配置值，平台給 W） */
  watts(w: number | null | undefined, digits?: number): string;
  money(m: { currency: string; amount: number } | null | undefined): string;
  /** 預估值（2 位有效數字）：「約 12,000 kWh」 */
  approxEnergy(mwh: number): string;
  /** 比較（VMS ▲▼）：回顯示字與朗讀字 */
  compare(ratio: number | null, prevMwh: number, curMwh: number): { text: string; aria: string; flat: boolean };
  /** 碳排：「3,171 kg CO2e」 */
  carbon(kg: number, gas: 'co2e' | 'co2'): string;
  /** 整數，帶千分位 */
  int(n: number): string;
}

const CURRENCY: Record<string, { sym: string; space: boolean; digits: number }> = {
  TWD: { sym: 'NT$', space: true, digits: 0 },
  JPY: { sym: '¥', space: false, digits: 0 },
  CNY: { sym: 'CN¥', space: true, digits: 2 },
  USD: { sym: 'US$', space: true, digits: 2 },
  EUR: { sym: '€', space: false, digits: 2 },
};

function nf(lang: string, min: number, max: number): Intl.NumberFormat {
  return new Intl.NumberFormat(lang === 'zh-Hant' ? 'zh-TW' : lang === 'zh-Hans' ? 'zh-CN' : lang, {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  });
}

/** kWh 數值 → 顯示用的數字（照位數規則）；< 0.01 回 null（呼叫端寫「不到 0.01」）。 */
export function kwhDigits(kwh: number): number | null {
  const a = Math.abs(kwh);
  if (a === 0) return 2;
  if (a < 0.01) return null;
  if (a < 10) return 2;
  if (a < 1000) return 1;
  return 0;
}

/** 2 位有效數字、至少到個位。 */
export function approxValue(v: number): number {
  if (v <= 0) return 0;
  if (v < 10) return Math.max(1, Math.round(v));
  const mag = Math.pow(10, Math.floor(Math.log10(v)) - 1);
  return Math.round(v / mag) * mag;
}

/** 百分比顯示：無條件捨去（誠實顯示：94.7% 寫 94%）。 */
export function pctFloor(part: number, whole: number): number {
  if (!whole) return 0;
  return Math.floor((part / whole) * 100 + 1e-9);
}

/** 原始值是否 ≥ 門檻（用原始值判斷，不用捨入後的百分比）。 */
export function overThreshold(part: number, whole: number, pct: number): boolean {
  return whole > 0 && part * 100 >= whole * pct;
}

export function makeFmt(t: TFunction, lang: string, unit: EnergyUnit): Fmt {
  const unitText = t(unit === 'du' ? 'energy:unit.du' : 'energy:unit.kwh');
  const lessThan = (v: string) => t('energy:fmt.less_than', { v });
  const energyNum = (mwh: number | null | undefined): string => {
    if (mwh === null || mwh === undefined || !Number.isFinite(mwh)) return '—';
    const kwh = mwh / 1e6;
    const d = kwhDigits(kwh);
    if (d === null) return lessThan('0.01');
    return nf(lang, d, d).format(kwh);
  };
  const energy = (mwh: number | null | undefined): string => {
    if (mwh === null || mwh === undefined || !Number.isFinite(mwh)) return '—';
    const kwh = mwh / 1e6;
    if (kwhDigits(kwh) === null) return lessThan(`0.01${NBSP}${unitText}`);
    return `${energyNum(mwh)}${NBSP}${unitText}`;
  };
  const watts = (w: number | null | undefined, digits?: number): string => {
    if (w === null || w === undefined || !Number.isFinite(w)) return '—';
    if (digits !== undefined) return `${nf(lang, digits, digits).format(w)}${NBSP}W`;
    if (w > 0 && w < 1) return lessThan(`1${NBSP}W`);
    const d = w < 10 && w !== 0 ? 1 : 0;
    return `${nf(lang, d, d).format(w)}${NBSP}W`;
  };
  const power = (mw: number | null | undefined) => (mw === null || mw === undefined ? '—' : watts(mw / 1000));
  const money = (m: { currency: string; amount: number } | null | undefined): string => {
    if (!m || !Number.isFinite(m.amount)) return '—';
    const c = CURRENCY[m.currency] ?? { sym: m.currency, space: true, digits: 2 };
    const join = (num: string) => (c.space ? `${c.sym}${NBSP}${num}` : `${c.sym}${num}`);
    if (c.digits === 0 && m.amount > 0 && m.amount < 1) return lessThan(join('1'));
    return join(nf(lang, c.digits, c.digits).format(c.digits === 0 ? Math.round(m.amount) : m.amount));
  };
  const int = (n: number) => nf(lang, 0, 0).format(Math.round(n));
  const approxEnergy = (mwh: number) => t('energy:fmt.approx', { v: `${int(approxValue(mwh / 1e6))}${NBSP}${unitText}` });
  const compare = (ratio: number | null, prevMwh: number, curMwh: number) => {
    // 上一期 < 1 度：改寫絕對差「多 0.3 度」
    if (ratio === null || prevMwh < 1e6) {
      const diff = curMwh - prevMwh;
      if (Math.abs(diff) < 5e3) return { text: t('energy:cmp.flat'), aria: t('energy:cmp.flat'), flat: true };
      const v = energy(Math.abs(diff));
      const key = diff > 0 ? 'energy:cmp.more_abs' : 'energy:cmp.less_abs';
      return { text: t(key, { v }), aria: t(key, { v }), flat: false };
    }
    const pct = Math.round(Math.abs(ratio) * 100);
    if (pct === 0) return { text: t('energy:cmp.flat'), aria: t('energy:cmp.flat'), flat: true };
    const up = ratio > 0;
    return {
      text: `${up ? '▲' : '▼'}${NBSP}${pct}%`,
      aria: t(up ? 'energy:cmp.more_pct' : 'energy:cmp.less_pct', { pct }),
      flat: false,
    };
  };
  const carbon = (kg: number, gas: 'co2e' | 'co2') => `${int(kg)}${NBSP}kg${NBSP}${gas === 'co2e' ? 'CO2e' : 'CO2'}`;
  return { lang, unit, unitText, energy, energyNum, power, watts, money, approxEnergy, compare, carbon, int };
}

const partsFmt = new Map<string, Intl.DateTimeFormat>();
/** ISO 時間在某時區的年月日時分（圖表標籤、說明列用）。 */
export function localParts(iso: string | number, tz?: string): { y: number; m: number; d: number; h: number; min: number; wd: number } {
  const key = tz ?? '';
  let f = partsFmt.get(key);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', weekday: 'short' });
    } catch {
      f = new Intl.DateTimeFormat('en-US', { hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', weekday: 'short' });
    }
    partsFmt.set(key, f);
  }
  const p = Object.fromEntries(f.formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  const wd = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(p.weekday) + 1;
  return { y: +p.year, m: +p.month, d: +p.day, h: +p.hour, min: +p.minute, wd };
}

/** 「14:35」。 */
export function hhmm(iso: string | number, tz?: string): string {
  const p = localParts(iso, tz);
  return `${String(p.h).padStart(2, '0')}:${String(p.min).padStart(2, '0')}`;
}

/** 時區的顯示名（「台北時間（UTC+8）」由字串表給；這裡回 UTC 位移）。 */
export function utcOffsetLabel(tz?: string, at = Date.now()): string {
  const p = localParts(at, tz);
  const local = Date.UTC(p.y, p.m - 1, p.d, p.h, p.min);
  const off = Math.round((local - Math.floor(at / 60_000) * 60_000) / 60_000);
  const h = Math.trunc(off / 60);
  const m = Math.abs(off % 60);
  return `UTC${off >= 0 ? '+' : '−'}${Math.abs(h)}${m ? ':' + String(m).padStart(2, '0') : ''}`;
}
